import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Switch,
} from "@material-ui/core";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import useModules from "../../hooks/useModules";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  tableContainer: {
    width: "100%",
    overflowX: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const PlanModulesManager = () => {
  const classes = useStyles();
  const { list: listPlans } = usePlans();
  const { getPlanModules, updatePlanModules } = useModules();

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const planList = await listPlans();
        setPlans(planList);
      } catch (e) {
        toast.error(i18n.t("planModulesManager.toasts.plansLoadError"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedPlan) return;
    (async () => {
      setLoading(true);
      try {
        const modulesData = await getPlanModules(selectedPlan);
        setModules(modulesData);
      } catch (e) {
        toast.error(i18n.t("planModulesManager.toasts.modulesLoadError"));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan]);

  const handleToggle = (id, value) => {
    setModules((prev) =>
      prev.map((m) => (m.id === id ? { ...m, included: value } : m))
    );
  };

  const handleSave = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      const payload = modules.map((m) => ({
        moduleId: m.id,
        key: m.key,
        included: m.included !== false,
      }));
      await updatePlanModules(selectedPlan, payload);
      toast.success(i18n.t("planModulesManager.toasts.saved"));
    } catch (e) {
      toast.error(i18n.t("planModulesManager.toasts.saveError"));
    }
    setLoading(false);
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} md={4} item>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{i18n.t("planModulesManager.form.plan")}</InputLabel>
            <Select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              label={i18n.t("planModulesManager.form.plan")}
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} item>
          <Paper className={classes.tableContainer}>
            <Table padding="none">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("planModulesManager.table.module")}</TableCell>
                  <TableCell>{i18n.t("planModulesManager.table.category")}</TableCell>
                  <TableCell align="center">{i18n.t("planModulesManager.table.included")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.category}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={m.included !== false}
                        onChange={(e) => handleToggle(m.id, e.target.checked)}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid xs={12} item>
          <ButtonWithSpinner
            variant="contained"
            color="primary"
            onClick={handleSave}
            loading={loading}
          >
            {i18n.t("planModulesManager.buttons.saveChanges")}
          </ButtonWithSpinner>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PlanModulesManager;
